'use client';

import { useEffect, useState } from 'react';

export type NotificationVariant = 'success' | 'delete' | 'error';

export interface Notification {
  id: string;
  title: string;
  description?: string;
  variant: NotificationVariant;
  action?: { label: string; onClick: () => void };
  duration: number;
}

type Listener = (notifications: Notification[]) => void;

let notifications: Notification[] = [];
let listeners: Listener[] = [];

function emit() {
  listeners.forEach((l) => l([...notifications]));
}

function removeNotification(id: string) {
  notifications = notifications.filter((n) => n.id !== id);
  emit();
}

function addNotification(n: Notification) {
  notifications = [...notifications, n];
  emit();

  if (n.duration > 0) {
    setTimeout(() => removeNotification(n.id), n.duration);
  }
}

let counter = 0;

export function notify(
  params: Omit<Notification, 'id' | 'duration'> & { duration?: number }
): string {
  const id = `lib-notif-${++counter}`;
  addNotification({
    ...params,
    id,
    duration: params.duration ?? 4000,
  });
  return id;
}

export function notifySaveSuccess(title: string) {
  notify({
    title: 'Tersimpan',
    description: `"${title}" berhasil disimpan ke perpustakaan.`,
    variant: 'success',
    duration: 4000,
  });
}

export function notifySave(title: string, onUndo: () => void) {
  notify({
    title: 'Tersimpan',
    description: `"${title}" berhasil disimpan ke perpustakaan.`,
    variant: 'success',
    duration: 8000,
    action: { label: 'Urungkan', onClick: onUndo },
  });
}

export function notifyDelete(title: string, onUndo: () => void) {
  notify({
    title: 'Dihapus dari perpustakaan',
    description: `"${title}" telah dihapus.`,
    variant: 'delete',
    duration: 8000,
    action: { label: 'Urungkan', onClick: onUndo },
  });
}

export function notifyError(title: string, description?: string) {
  notify({
    title,
    description: description ?? 'Terjadi kesalahan. Silakan coba lagi.',
    variant: 'error',
    duration: 5000,
  });
}

export function useNotifications(): Notification[] {
  const [state, setState] = useState<Notification[]>([]);

  useEffect(() => {
    listeners.push(setState);
    setState([...notifications]);
    return () => {
      listeners = listeners.filter((l) => l !== setState);
    };
  }, []);

  return state;
}

export function useDismissNotification() {
  return removeNotification;
}
